import { LoginComponent } from "./features/auth/login/login.component";
import { ForbiddenComponent } from "./features/auth/forbidden/forbidden.component";
import { NoAccessComponent } from "./features/auth/no-access/no-access.component";
import { SessionExpiredComponent } from "./features/auth/session-expired/session-expired.component";
import { UnknownOrganizationComponent } from "./features/auth/unknown-organization/unknown-organization.component";
import { AppShellComponent } from "./shared/components/app-shell/app-shell.component";
import { authGuard } from "./core/guards/auth.guard";
import { orgGuard } from "./core/guards/org.guard";
import { davacoAdminGuard } from "./core/guards/davaco-admin.guard";
import { staffAccessGuard } from "./core/guards/staff-access.guard";
import { customerAdminGuard } from "./core/guards/customer-admin.guard";
import { featureGuard } from "./core/guards/feature.guard";
import { passwordChangeGuard, setPasswordPageGuard } from "./core/guards/password-change.guard";
import { unsavedChangesGuard } from "./core/guards/unsaved-changes.guard";
import { PREVIEW_ROUTES } from "./preview/preview.routes";

import type { Routes } from "@angular/router";

export const routes: Routes = [
  // Empty outside local development builds (see preview.routes.ts).
  ...PREVIEW_ROUTES,
  { path: "login", component: LoginComponent },
  { path: "forbidden", component: ForbiddenComponent },
  // Outside the shell, like every other refusal: the shell is exactly what
  // this visitor may not have, and routing them into it to say so would run
  // the guard that sent them here.
  { path: "no-access", component: NoAccessComponent },
  { path: "session-expired", component: SessionExpiredComponent },
  // Board 7f right. Unguarded on purpose: it is where a host with no
  // organization behind it lands, so it must render before any token exists.
  { path: "unknown-organization", component: UnknownOrganizationComponent },
  // Outside the shell: a customer holding a temporary password replaces it
  // here before they reach anything else (branded-login DECISION-009).
  {
    path: "set-password",
    canActivate: [setPasswordPageGuard],
    loadComponent: () =>
      import("./features/auth/set-password/set-password.component").then(
        (m) => m.SetPasswordComponent
      ),
  },
  {
    path: "",
    component: AppShellComponent,
    // Order matters. authGuard first, so an anonymous visitor is sent to
    // /login rather than told they lack a role they were never asked for;
    // staffAccessGuard is the narrowest of the access checks. passwordChangeGuard
    // runs after all of them: only someone allowed in is asked to set a password.
    canActivate: [authGuard, orgGuard, staffAccessGuard, passwordChangeGuard],
    children: [
      {
        path: "dashboard",
        loadComponent: () =>
          import("./features/dashboard/dashboard.component")
.then((m) => m.DashboardComponent),
      },
      {
        path: "reports",
        loadComponent: () =>
          import("./features/reports/reports.component")
.then((m) => m.ReportsComponent),
      },
      // Before "reports" would be wrong — Angular matches in order, but these
      // are distinct paths, not a prefix pair, so order is irrelevant here.
      // Kept beside Reports for readability.
      {
        path: "reports/new",
        loadComponent: () =>
          import("./features/report-builder/report-builder.component")
.then((m) => m.ReportBuilderComponent),
      },
      // AFTER "reports/new" on purpose: Angular matches in order, and
      // ":id" would otherwise swallow "new" and try to open a saved
      // report with that id.
      {
        path: "reports/:id",
        loadComponent: () =>
          import("./features/saved-report/saved-report.component")
.then((m) => m.SavedReportComponent),
      },
      // Its own page, not a mode of the one above: that stays a reader. The
      // guard asks before leaving with unsaved edits.
      {
        path: "reports/:id/edit",
        loadComponent: () =>
          import("./features/report-edit/report-edit.component")
            .then((m) => m.ReportEditComponent),
        canDeactivate: [unsavedChangesGuard],
      },
      // One survey, read-only, opened from the dashboard's Sites table. The
      // API decides whether the caller may see it; the id is only which one.
      {
        path: "surveys/:id",
        loadComponent: () =>
          import("./features/survey-instance/survey-instance.component")
            .then((m) => m.SurveyInstanceComponent),
      },
      {
        path: "surveys/:id/media",
        loadComponent: () =>
          import("./features/survey-instance/survey-media/survey-media.component")
            .then((m) => m.SurveyMediaComponent),
      },
      // Optional pages: an organization can switch each off in Settings.
      {
        path: "documents",
        canActivate: [featureGuard("documents")],
        loadComponent: () =>
          import("./features/documents/documents.component")
.then((m) => m.DocumentsComponent),
      },
      {
        path: "activity",
        canActivate: [featureGuard("activity")],
        loadComponent: () =>
          import("./features/activity/activity.component")
.then((m) => m.ActivityComponent),
      },
      // A customer admin's own organization. The API re-checks the role.
      {
        path: "members",
        canActivate: [customerAdminGuard],
        loadComponent: () =>
          import("./features/members/members-page/members-page.component").then(
            (m) => m.MembersPageComponent
          ),
      },
      {
        path: "settings",
        loadComponent: () =>
          import("./features/settings/settings.component")
.then((m) => m.SettingsComponent),
      },

      // Davaco-only. The guard is a UI affordance; customer-portal-api refuses
      // every one of these routes for a customer token regardless.
      {
        path: "admin/organizations",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/organizations/organizations.component")
.then(
            (m) => m.AdminOrganizationsComponent
          ),
      },
      {
        path: "admin/onboarding",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/onboarding/onboarding.component")
.then(
            (m) => m.OnboardingComponent
          ),
      },
      {
        path: "admin/auth0-sync",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/organization-delta/organization-delta.component")
.then(
            (m) => m.OrganizationDeltaComponent
          ),
      },
      // Edit views, separate from the read-only organization page (#705).
      {
        path: "admin/organizations/:slug/branding/edit",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/branding-edit/branding-edit.component")
.then((m) => m.BrandingEditComponent),
      },
      {
        path: "admin/organizations/:slug/sign-in/edit",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/sign-in-edit/sign-in-edit.component")
.then((m) => m.SignInEditComponent),
      },
      {
        path: "admin/organizations/:slug",
        canActivate: [davacoAdminGuard],
        loadComponent: () =>
          import("./features/admin/organization-detail/organization-detail.component")
.then(
            (m) => m.OrganizationDetailComponent
          ),
      },

      { path: "", redirectTo: "dashboard", pathMatch: "full" },

      // A real 404 inside the shell. Previously every unmatched path fell
      // through a top-level wildcard back to the dashboard, so a mistyped or
      // stale link silently looked like it had worked.
      {
        path: "**",
        loadComponent: () =>
          import("./features/errors/not-found.component")
.then((m) => m.NotFoundComponent),
      },
    ],
  },
];
